// src/lib/ideInstall.ts
//
// Pure, framework-agnostic helpers that turn the resolved MCP base URL into
// per-IDE install affordances (deep links and copy-config fallbacks).
//
// This module currently defines the core data types and the shared
// `IDE_REGISTRY`. Config-JSON rendering, deep-link generators, and the
// `buildInstallTarget` orchestrator are added by later tasks (3.1, 4.x, 5.1).

import type { CatalogEntry } from './catalog'

/** Stable identifiers for every IDE we support. */
export type IdeId = 'vscode' | 'vscode-insiders' | 'cursor' | 'kiro' | 'claude'

/** How a given IDE is installed. */
export type InstallKind = 'deeplink' | 'copy'

/**
 * The effective install method for one (IDE, entry) pair: the deeplink-vs-copy
 * decision actually used to render the affordance, derived from the IDE's static
 * `kind` and the entry's `serverKind` (see `deriveEffectiveMethod`).
 */
export type EffectiveInstallMethod = 'deeplink' | 'copy'

/** Static metadata for one IDE (the "IDE registry"). */
export interface IdeDescriptor {
  id: IdeId
  /** Human-readable label, e.g. "VS Code". */
  label: string
  /** Whether the IDE supports one-click deep-link install or copy-config only. */
  kind: InstallKind
  /** Config file path shown for the copy fallback / advanced view. */
  configFilePath: string
  /** How the developer supplies their token, shown as a note. */
  tokenNote: string
}

/** Everything the UI needs to render one IDE's install affordance. */
export interface InstallTarget {
  ide: IdeDescriptor
  /** The catalog entry this affordance was built from. */
  entry: CatalogEntry
  /** The deeplink-vs-copy decision actually used (see `deriveEffectiveMethod`). */
  effectiveMethod: EffectiveInstallMethod
  /** Present only when `effectiveMethod === 'deeplink'`. */
  deepLinkUrl?: string
  /** Pretty-printed JSON for the copy block (always present). */
  configJson: string
  /** Optional CLI command (e.g. Claude Code `claude mcp add ...`). */
  cliCommand?: string
}

/**
 * The single shared source of truth for every supported IDE, in a fixed,
 * stable order: VS Code, VS Code Insiders, Cursor, Kiro, Claude Code.
 */
export const IDE_REGISTRY: readonly IdeDescriptor[] = [
  {
    id: 'vscode',
    label: 'VS Code',
    kind: 'deeplink',
    configFilePath: '.vscode/mcp.json',
    tokenNote: 'Prompted on first connect and stored in VS Code secret storage via ${input:sdlcToken}.',
  },
  {
    id: 'vscode-insiders',
    label: 'VS Code Insiders',
    kind: 'deeplink',
    configFilePath: '.vscode/mcp.json',
    tokenNote: 'Prompted on first connect and stored in VS Code secret storage via ${input:sdlcToken}.',
  },
  {
    id: 'cursor',
    label: 'Cursor',
    kind: 'deeplink',
    configFilePath: '.cursor/mcp.json',
    tokenNote: 'See token setup instructions below.',
  },
  {
    id: 'kiro',
    label: 'Kiro',
    kind: 'copy',
    configFilePath: '.kiro/settings/mcp.json',
    tokenNote: 'See token setup instructions below.',
  },
  {
    id: 'claude',
    label: 'Claude Code',
    kind: 'copy',
    configFilePath: '.mcp.json',
    tokenNote: 'See token setup instructions below.',
  },
] as const

/** True iff the IDE uses the `servers` (+ `inputs`) top-level key (VS Code family). */
function usesServersKey(ide: IdeDescriptor): boolean {
  return ide.id === 'vscode' || ide.id === 'vscode-insiders'
}

/**
 * Collect the distinct `${input:NAME}` placeholder names referenced by the
 * values of an environment-variable map, in first-appearance order. Used to
 * synthesize the VS Code `inputs` array for `local-command` entries so VS Code
 * prompts the developer for each referenced value.
 */
function collectInputNames(env: Record<string, string>): string[] {
  const names: string[] = []
  const seen = new Set<string>()
  const pattern = /\$\{input:([^}]+)\}/g

  for (const value of Object.values(env)) {
    let match: RegExpExecArray | null
    pattern.lastIndex = 0
    while ((match = pattern.exec(value)) !== null) {
      const name = match[1]
      if (!seen.has(name)) {
        seen.add(name)
        names.push(name)
      }
    }
  }

  return names
}

/** Build one VS Code `promptString` input descriptor per distinct placeholder name. */
function buildInputs(names: string[]): { id: string; type: string; password: boolean; description: string }[] {
  return names.map((name) => ({
    id: name,
    type: 'promptString',
    password: true,
    description: `Enter the value for ${name}`,
  }))
}

/**
 * Render the pretty-printed Config_JSON for one IDE and one `CatalogEntry`.
 *
 * The single server entry is keyed by `entry.id` and embeds either the resolved
 * base URL (`remote-http`) or the entry's command/args/env (`local-command`).
 * Tokens are referenced only through Token_Placeholders — never literal values.
 *
 * - VS Code / VS Code Insiders use the `servers` (+ `inputs`) top-level key;
 *   Cursor / Kiro / Claude Code use the `mcpServers` key.
 * - `remote-http` emits the `http` transport + `url: baseUrl` + a per-IDE bearer
 *   placeholder (`${input:sdlcToken}` for VS Code, `${env:SDLC_TOKEN}` for
 *   Cursor, `${SDLC_TOKEN}` for Kiro / Claude Code).
 * - `local-command` emits `{ command, args, env }` with the env map passed
 *   through verbatim (already placeholders); for VS Code it additionally
 *   synthesizes an `inputs` array from any `${input:NAME}` references in the env
 *   values.
 *
 * Pure and total: deterministic, no mutation, no side effects.
 */
export function renderConfigJson(ide: IdeDescriptor, entry: CatalogEntry, baseUrl: string): string {
  let obj: unknown

  if (entry.serverKind === 'local-command') {
    const serverEntry = {
      command: entry.command,
      args: entry.args ?? [],
      env: entry.env ?? {},
    }

    if (usesServersKey(ide)) {
      const inputNames = collectInputNames(entry.env ?? {})
      obj =
        inputNames.length > 0
          ? { servers: { [entry.id]: serverEntry }, inputs: buildInputs(inputNames) }
          : { servers: { [entry.id]: serverEntry } }
    } else {
      obj = { mcpServers: { [entry.id]: serverEntry } }
    }

    return JSON.stringify(obj, null, 2)
  }

  // remote-http
  switch (ide.id) {
    case 'vscode':
    case 'vscode-insiders':
      obj = {
        servers: {
          [entry.id]: {
            type: 'http',
            url: baseUrl,
            headers: { Authorization: 'Bearer ${input:sdlcToken}' },
          },
        },
        inputs: [
          {
            id: 'sdlcToken',
            type: 'promptString',
            password: true,
            description: `KONE SDLC bearer token (copy from ${baseUrl} -> Profile)`,
          },
        ],
      }
      break
    case 'cursor':
      obj = {
        mcpServers: {
          [entry.id]: {
            url: baseUrl,
            headers: { Authorization: 'Bearer ${env:SDLC_TOKEN}' },
          },
        },
      }
      break
    case 'kiro':
    case 'claude':
      obj = {
        mcpServers: {
          [entry.id]: {
            url: baseUrl,
            headers: { Authorization: 'Bearer ${SDLC_TOKEN}' },
          },
        },
      }
      break
  }

  return JSON.stringify(obj, null, 2)
}

/**
 * Derive the Effective_Install_Method for one (IDE, entry) pair — the single
 * source of truth for the deeplink-vs-copy decision (Requirements 4.7, 4.8).
 *
 * Returns `'deeplink'` iff the IDE statically supports deep links
 * (`ide.kind === 'deeplink'`) AND the entry is a remote HTTP server
 * (`entry.serverKind === 'remote-http'`); otherwise `'copy'`. Local-command
 * entries and copy-only IDEs (Kiro, Claude Code) always fall back to copy.
 *
 * Pure and total: deterministic, no mutation, no side effects.
 */
export function deriveEffectiveMethod(ide: IdeDescriptor, entry: CatalogEntry): EffectiveInstallMethod {
  return ide.kind === 'deeplink' && entry.serverKind === 'remote-http' ? 'deeplink' : 'copy'
}

/**
 * Build the VS Code (or VS Code Insiders) one-click install deep link.
 *
 * The link carries a single URL-encoded JSON payload — `encodeURIComponent` over
 * the full JSON string, NOT base64 — that VS Code's `mcp/install` URI handler
 * consumes to register the server. The payload names the server `entry.id`,
 * declares the HTTP transport pointing at the resolved base URL, and references
 * the token only through the `${input:sdlcToken}` placeholder (prompted on first
 * connect and stored in secret storage) — never a literal value.
 *
 * The scheme is `vscode-insiders:` when `insiders` is true, otherwise `vscode:`.
 *
 * Round-trip safe: `JSON.parse(decodeURIComponent(query))` recovers the exact
 * payload, with `name === entry.id` and `url === baseUrl`.
 *
 * Pure and total: deterministic, no mutation, no side effects.
 */
export function buildVsCodeDeepLink(entry: CatalogEntry, baseUrl: string, insiders: boolean): string {
  const payload = {
    name: entry.id,
    type: 'http',
    url: baseUrl,
    headers: { Authorization: 'Bearer ${input:sdlcToken}' },
    inputs: [
      {
        id: 'sdlcToken',
        type: 'promptString',
        password: true,
        description: `KONE SDLC bearer token (copy from ${baseUrl} -> Profile)`,
      },
    ],
  }

  const scheme = insiders ? 'vscode-insiders' : 'vscode'
  return `${scheme}:mcp/install?${encodeURIComponent(JSON.stringify(payload))}`
}

/**
 * UTF-8-safe base64 encoder for use by the Cursor deep-link generator (task 4.3).
 *
 * The browser's `btoa` operates on Latin-1 code points and throws on characters
 * outside that range. Running the string through `encodeURIComponent` first turns
 * any non-ASCII into ASCII `%XX` escapes, and `unescape` collapses those escapes
 * into the individual UTF-8 bytes (each within Latin-1 range) that `btoa` can then
 * encode. The result is standard base64 whose UTF-8 decode recovers `s` exactly.
 *
 * Pure and total: deterministic, no mutation, no side effects, never throws for
 * any string input (including non-ASCII).
 */
export function toBase64Utf8(s: string): string {
  return btoa(unescape(encodeURIComponent(s)))
}

/**
 * Build the Cursor one-click install deep link.
 *
 * Cursor's `mcp/install` deep link carries the server name as a plain query
 * parameter and the transport config as a base64-encoded JSON payload. Unlike the
 * VS Code link, the encoded `config` object holds ONLY the transport fields
 * (`url` + `headers`) and carries NO `name` key. The token is referenced solely
 * through the `${env:SDLC_TOKEN}` placeholder — never a literal value.
 *
 * Round-trip safe: base64-decoding the `config` query param and running it
 * through `JSON.parse` recovers `{ url: baseUrl, headers: {...} }` exactly.
 *
 * Pure and total: deterministic, no mutation, no side effects.
 */
export function buildCursorDeepLink(entry: CatalogEntry, baseUrl: string): string {
  const transport = {
    url: baseUrl,
    headers: { Authorization: 'Bearer ${env:SDLC_TOKEN}' },
  }

  const b64 = toBase64Utf8(JSON.stringify(transport))
  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(entry.id)}&config=${b64}`
}

/** Regex over the full config string for a literal `Bearer <value>` token. */
const LITERAL_BEARER = /Bearer\s+(?!\$\{)/

/** Key names that designate a credential-bearing value (token / secret / key / password). */
const CREDENTIAL_KEY = /(token|secret|password|passwd|api[_-]?key|apikey|credential)/i

/** A `${...}` placeholder reference, treated as a safe (non-literal) value. */
const PLACEHOLDER = /\$\{[^}]+\}/

/** Angle-bracket placeholder like <REPLACE_WITH_YOUR_TOKEN>, treated as safe. */
const ANGLE_PLACEHOLDER = /^<[A-Z_]+>$/

/**
 * Recursively scan a parsed config tree for a credential-bearing key whose value
 * is a literal (i.e. carries no `${...}` placeholder). Non-secret values such as
 * URLs and usernames are ignored because their keys do not match CREDENTIAL_KEY.
 */
function scanForLiteralCredential(node: unknown): boolean {
  if (Array.isArray(node)) {
    return node.some(scanForLiteralCredential)
  }

  if (node !== null && typeof node === 'object') {
    for (const [key, value] of Object.entries(node)) {
      if (typeof value === 'string') {
        if (CREDENTIAL_KEY.test(key) && value.trim() !== '' && !PLACEHOLDER.test(value) && !ANGLE_PLACEHOLDER.test(value)) {
          return true
        }
      } else if (scanForLiteralCredential(value)) {
        return true
      }
    }
  }

  return false
}

/**
 * Returns true when `configJson` contains a literal token anywhere — i.e. a
 * credential value embedded directly rather than referenced through a `${...}`
 * placeholder. Two surfaces are scanned:
 *
 * - `Authorization` headers: a `Bearer <value>` whose value is not a `${...}`
 *   placeholder (the `remote-http` bearer surface).
 * - environment-variable maps: any credential-bearing key (its name mentions
 *   token / secret / key / password) whose value is not a `${...}` placeholder
 *   (the `local-command` surface). Non-secret env values (URLs, usernames) are
 *   ignored.
 *
 * A well-formed config references every token only through a Token_Placeholder,
 * so an all-placeholder config returns false.
 *
 * Pure and total: deterministic, never throws.
 */
function containsLiteralToken(configJson: string): boolean {
  if (LITERAL_BEARER.test(configJson)) {
    return true
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(configJson)
  } catch {
    return false
  }

  return scanForLiteralCredential(parsed)
}

/** The URI-scheme prefix each deep-link IDE's install link must begin with. */
function ideScheme(id: IdeId): string {
  switch (id) {
    case 'vscode':
      return 'vscode:'
    case 'vscode-insiders':
      return 'vscode-insiders:'
    case 'cursor':
      return 'cursor://'
    default:
      return ''
  }
}

/**
 * Build the per-IDE install affordance from a `CatalogEntry` and resolved base URL.
 *
 * Derives the Effective_Install_Method for the (IDE, entry) pair, renders the
 * IDE/kind-shaped Config_JSON (token placeholder only), and — only when the
 * effective method is `deeplink` — the IDE-native install deep link. Claude Code
 * additionally gets a `claude mcp add` CLI one-liner: the `--transport http` form
 * for `remote-http` entries, or the `-- <command> <args>` form for
 * `local-command` entries.
 *
 * Follows the design's ALGORITHM buildInstallTarget and its postconditions:
 * `entry`/`ide`/`effectiveMethod` are echoed back, the resolved base URL is
 * embedded in `configJson` (remote-http) and in `deepLinkUrl` (deep-link
 * targets), the rendered config never contains a literal token, and `deepLinkUrl`
 * is defined iff `effectiveMethod === 'deeplink'`.
 *
 * Pure and total for valid inputs.
 *
 * @throws if a `remote-http` entry's `baseUrl` is not an absolute `https://` URL.
 * @throws if the rendered config unexpectedly contains a literal token.
 */
export function buildInstallTarget(ide: IdeDescriptor, entry: CatalogEntry, baseUrl: string): InstallTarget {
  const method = deriveEffectiveMethod(ide, entry)

  if (entry.serverKind === 'remote-http' && (typeof baseUrl !== 'string' || !baseUrl.startsWith('https://'))) {
    throw new Error(`buildInstallTarget: baseUrl must be an absolute https:// URL, got: ${baseUrl}`)
  }

  const configJson = renderConfigJson(ide, entry, baseUrl)

  if (containsLiteralToken(configJson)) {
    throw new Error('buildInstallTarget: rendered config contains a literal token')
  }

  let deepLinkUrl: string | undefined
  let cliCommand: string | undefined

  if (method === 'deeplink') {
    switch (ide.id) {
      case 'vscode':
        deepLinkUrl = buildVsCodeDeepLink(entry, baseUrl, false)
        break
      case 'vscode-insiders':
        deepLinkUrl = buildVsCodeDeepLink(entry, baseUrl, true)
        break
      case 'cursor':
        deepLinkUrl = buildCursorDeepLink(entry, baseUrl)
        break
    }

    const scheme = ideScheme(ide.id)
    if (deepLinkUrl === undefined || !deepLinkUrl.startsWith(scheme)) {
      throw new Error(`buildInstallTarget: deep link does not begin with the ${ide.id} scheme`)
    }
  }

  if (ide.id === 'claude') {
    if (entry.serverKind === 'remote-http') {
      cliCommand = `claude mcp add --transport http ${entry.id} ${baseUrl}`
    } else {
      const args = (entry.args ?? []).join(' ')
      cliCommand = `claude mcp add ${entry.id} -- ${entry.command} ${args}`.trimEnd()
    }
  }

  return { ide, entry, effectiveMethod: method, deepLinkUrl, configJson, cliCommand }
}
